/* The current task's sub-tasks as one lane of touching blocks (5.1).
 *
 * THE ZOOM ORDER MATTERS. Requirement 5.1.3 gives a floor of ~26 px/second so
 * short sub-tasks stay readable, and a zoom range of ×1 to ×4 of fit-to-width.
 * The floor is applied FIRST and the zoom multiplies the result:
 *
 *     pxPerSecond = max(available / span, 26) * zoom
 *
 * Applying the floor last instead — max(available / span * zoom, 26) — makes
 * zoom a no-op on any long task, because the floor already exceeds the fitted
 * width and swallows the multiplier. The prototype had this right; getting it
 * backwards is a silent dead control, not a visible bug.
 *
 * BLOCKS TOUCH BY CONSTRUCTION. Each block's width comes from its own span, and
 * the spans tile because the server guarantees they do. Nothing here adds a
 * gap for spacing — a 1px margin between blocks would draw a gap that the data
 * does not have, and annotators would report it as one.
 */

import { useEffect, useLayoutEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import type { Subtask, Task } from "../api/types";
import { HAND_GLYPH, clockTenths, preciseClock, subtaskTitle } from "../lib/format";
import { MARK_LABEL, needsAPerson, wasCorrected } from "../lib/verdict";

/* WIDE ENOUGH TO READ, which 26 was not.
 *
 * A block's width IS its duration — that is what makes this lane a map of the
 * video rather than a list. At 26px a second, a two-second sub-task was 52px:
 * enough for "2" and three letters of its title before the ellipsis, so the
 * text was there and unreadable, which is worse than absent because it costs
 * the space and gives nothing back.
 *
 * 48 makes that same block 96px — two or three words — and every block scales
 * with it, so the proportionality the lane depends on is untouched. The cost
 * is a longer lane and more horizontal scrolling, which is the right trade: it
 * already scrolls, and a strip you have to hover to read is not doing its job.
 *
 * NOT a per-block minimum width. Giving short sub-tasks a floor would break
 * the one property that makes the lane worth having — blocks would stop
 * lining up with the ruler above them, and a 0.5s block would look like a 2s
 * one. Zoom is the control for reading a crowded stretch. */
const MIN_PX_PER_SECOND = 48; // 5.1.3
const MIN_ZOOM = 1;
const MAX_ZOOM = 4;
const EDGE_GRAB_PX = 6;
const MIN_BLOCK_SECONDS = 0.4; // 5.2.1 clamp

/** 5.1.1: tick spacing adapts to task length. */
function tickStep(span: number): number {
  if (span <= 60) return 5;
  if (span <= 150) return 15;
  return 30;
}

interface Props {
  task: Task;
  selectedId: number | null;
  playhead: number;
  onSelect: (sub: Subtask) => void;
  onSeek: (t: number) => void;
  /** Commits a boundary move. The server decides whether it is legal. */
  onMoveBoundary: (afterSubtaskId: number, at: number) => void;
  /** Split / Merge / Undo. They live on this row rather than up with the
   *  playback controls because they act on SUB-TASKS, which is what this row
   *  is about — and testing asked for them beside the sub-task count. */
  actions?: ReactNode;
}

export function Timeline({
  task, selectedId, playhead, onSelect, onSeek, onMoveBoundary, actions,
}: Props) {
  const wrap = useRef<HTMLDivElement>(null);
  const [avail, setAvail] = useState(800);
  const [zoom, setZoom] = useState(1);
  /** While dragging, the boundary is drawn from local state so the block edge
   *  tracks the pointer without a round trip. It is committed on release. */
  const [drag, setDrag] = useState<{ afterId: number; at: number } | null>(null);

  useLayoutEffect(() => {
    const el = wrap.current;
    if (!el) return;
    const ro = new ResizeObserver(([e]) => setAvail(e.contentRect.width));
    ro.observe(el);
    setAvail(el.clientWidth);
    return () => ro.disconnect();
  }, []);

  const span = Math.max(task.end_seconds - task.start_seconds, 0.001);
  // Floor first, then zoom. See the header comment.
  const pxPerSecond = Math.max(avail / span, MIN_PX_PER_SECOND) * zoom;
  const laneWidth = span * pxPerSecond;

  const xOf = (t: number) => (t - task.start_seconds) * pxPerSecond;
  const tOf = (x: number) => task.start_seconds + x / pxPerSecond;

  const subs = task.subtasks;

  // Live boundary override while dragging.
  const spanOf = (s: Subtask, i: number): [number, number] => {
    let start = s.start_seconds;
    let end = s.end_seconds;
    if (drag) {
      if (drag.afterId === s.id) end = drag.at;
      if (i > 0 && subs[i - 1].id === drag.afterId) start = drag.at;
    }
    return [start, end];
  };

  useEffect(() => {
    if (!drag) return;
    const move = (e: PointerEvent) => {
      const el = wrap.current;
      if (!el) return;
      const x = e.clientX - el.getBoundingClientRect().left + el.scrollLeft;
      const i = subs.findIndex((s) => s.id === drag.afterId);
      if (i < 0 || i === subs.length - 1) return;
      // Clamp locally so the edge cannot be dragged into a nonsense position;
      // the server clamps again, and its answer is the one that sticks.
      const lo = subs[i].start_seconds + MIN_BLOCK_SECONDS;
      const hi = subs[i + 1].end_seconds - MIN_BLOCK_SECONDS;
      setDrag({ afterId: drag.afterId, at: Math.min(Math.max(tOf(x), lo), hi) });
    };
    const up = () => {
      onMoveBoundary(drag.afterId, Number(drag.at.toFixed(3)));
      setDrag(null);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up, { once: true });
    return () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
    };
  }, [drag, subs, onMoveBoundary, pxPerSecond, task.start_seconds]);

  const step = tickStep(span);
  const ticks: number[] = [];
  for (let t = Math.ceil(task.start_seconds / step) * step; t < task.end_seconds; t += step)
    ticks.push(t);

  return (
    <div className="flex flex-col gap-1.5">
      {/* ONE ROW, THREE GROUPS: what this lane is (left), the sub-task actions
          (right, beside the zoom), the zoom (far right). Split / Merge / Undo
          used to sit inline after the drag hint, so three buttons trailed off
          the end of a sentence and the row read as prose with controls embedded
          in it. Right-aligned they group with the other controls on the row. */}
      <div className="flex items-center justify-between gap-3 text-[13px] text-muted">
        <span>
          <span className="text-ink">{subs.length} sub-tasks</span>
          {" · task "}{task.task_index}
          {/* "click empty space to seek" is gone — it describes the whole
              timeline's default behaviour, which needs no instruction, and it
              made the one hint that IS worth stating harder to spot. */}
          <span className="ml-3 text-muted">
            Drag an edge to move a shared boundary
          </span>
        </span>
        <span className="flex shrink-0 items-center gap-2">
          {actions}
          <span className="flex items-center gap-1">
            <button
              onClick={() => setZoom((z) => Math.max(MIN_ZOOM, z * 0.667))}
              disabled={zoom <= MIN_ZOOM}
              title="Zoom timeline out"
              className="rounded-chip bg-ground px-2 py-0.5 hover:bg-line disabled:opacity-40"
            >−</button>
            <span className="w-10 text-center font-mono">{zoom.toFixed(1)}×</span>
            <button
              onClick={() => setZoom((z) => Math.min(MAX_ZOOM, z * 1.5))}
              disabled={zoom >= MAX_ZOOM}
              title="Zoom timeline in"
              className="rounded-chip bg-ground px-2 py-0.5 hover:bg-line disabled:opacity-40"
            >+</button>
          </span>
        </span>
      </div>

      <div ref={wrap} className="lane-scroll overflow-x-auto overflow-y-hidden">
        <div style={{ width: laneWidth }} className="relative select-none">
          {/* ruler */}
          <div
            className="relative h-5 border-b border-line"
            onClick={(e) => {
              const el = wrap.current;
              if (!el) return;
              const x = e.clientX - el.getBoundingClientRect().left + el.scrollLeft;
              onSeek(Math.min(Math.max(tOf(x), task.start_seconds), task.end_seconds));
            }}
          >
            {ticks.map((t) => (
              <span
                key={t}
                style={{ left: xOf(t) }}
                className="absolute top-0 h-full border-l border-line pl-1
                  font-mono text-[11px] text-muted"
              >
                {Math.round(t)}s
              </span>
            ))}
          </div>

          {/* blocks — touching, no margins */}
          <div className="relative flex h-16">
            {subs.map((s, i) => {
              const [start, end] = spanOf(s, i);
              const w = (end - start) * pxPerSecond;
              const selected = s.id === selectedId;
              const tone =
                s.status === "APPROVED"
                  ? "bg-verdict-approved-bg text-verdict-approved-ink"
                  : s.status === "REJECTED"
                    ? "bg-verdict-rejected-bg text-verdict-rejected-ink"
                    : "bg-ground/80 text-ink";
              return (
                <div
                  key={s.id}
                  style={{ width: w }}
                  // Selecting must NOT move the playhead (5.1.4).
                  onClick={() => onSelect(s)}
                  className={`relative shrink-0 overflow-hidden border-r
                    border-card px-2 py-1.5 text-[13px] ${tone}
                    ${selected ? "ring-2 ring-inset ring-teal" : ""}`}
                  title={`${subtaskTitle(s, subs)} · ${preciseClock(start)} → ${preciseClock(end)}`}
                >
                  <div className="flex items-center gap-1 font-medium">
                    <span className="opacity-60">{s.subtask_index}</span>
                    <span className="truncate">{subtaskTitle(s, subs)}</span>
                  </div>
                  <div className="mt-0.5 flex items-center gap-1.5 opacity-70">
                    {/* A TICK ON WHAT IS DONE — testing, 17 Sep. The block's
                        tint already differed for approved, but tint alone is
                        weak on a lane of small coloured rectangles and invisible
                        to anyone who does not separate those greens. A glyph is
                        unambiguous at any size. */}
                    {s.status === "APPROVED" && (
                      <span className="text-verdict-approved-ink" title="Approved">✓</span>
                    )}
                    <span className="font-emoji" title={`Hands: ${s.hands}`}>{HAND_GLYPH[s.hands]}</span>
                    <span className="font-mono">{clockTenths(end - start)}</span>
                    {/* ▲ needs a person · ✎ the AI rewrote it · nothing when
                        the two models simply agreed. One glyph for both of the
                        first two put 75 marks on a lane the queue card called
                        10 — see lib/verdict.ts. */}
                    {needsAPerson(s.verdict) && (
                      <span className="text-verdict-flagged-ink"
                        title={MARK_LABEL["needs-person"]}>▲</span>
                    )}
                    {wasCorrected(s.verdict) && (
                      <span className="text-muted"
                        title={MARK_LABEL.corrected}>✎</span>
                    )}
                  </div>

                  {/* the shared edge, grabbable from the left block only so two
                      handles never sit on top of each other */}
                  {i < subs.length - 1 && (
                    <div
                      onPointerDown={(e) => {
                        e.stopPropagation();
                        setDrag({ afterId: s.id, at: end });
                      }}
                      style={{ width: EDGE_GRAB_PX * 2 }}
                      className="absolute inset-y-0 -right-1.5 z-10 cursor-col-resize
                        hover:bg-teal1"
                      title="Drag to move this boundary"
                    />
                  )}
                </div>
              );
            })}
          </div>

          {/* playhead */}
          {playhead >= task.start_seconds && playhead <= task.end_seconds && (
            <div
              style={{ left: xOf(playhead) }}
              className="pointer-events-none absolute inset-y-0 w-px bg-playhead"
            />
          )}
        </div>
      </div>
    </div>
  );
}
